// src/pages/GlucoseDiary.tsx
import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Select, InputNumber, Table, Typography, Row, Col, Modal, Space } from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import { diaryAPI } from '../utils/api';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { showToast } from '../utils/notifications';
import { useIsMobile } from '../hooks/useIsMobile';
import { decimalInputNumberProps, formatDecimalDisplay } from '../utils/decimalFormat';
import { formatDiaryDateTime, sortDiaryEntriesNewestFirst } from '../utils/diaryDisplay';

const { Title } = Typography;

export const GlucoseDiary: React.FC = () => {
  const isMobile = useIsMobile();
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [entries, setEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const mealTypes = [
    { label: 'Завтрак', value: 'breakfast' },
    { label: 'Обед', value: 'lunch' },
    { label: 'Ужин', value: 'dinner' },
    { label: 'Перекус', value: 'snack' },
  ];

  const measurementTimes = [
    { label: 'До еды', value: 'before' },
    { label: 'Через 1 час', value: '1h_after' },
    { label: 'Через 2 часа', value: '2h_after' },
  ];

  useEffect(() => {
    loadEntries();
  }, []);

  const loadEntries = async () => {
    try {
      setLoading(true);
      const response = await diaryAPI.getGlucoseEntries();
      setEntries(sortDiaryEntriesNewestFirst((response.data || []) as any[]));
    } catch (error: any) {
      console.error('Error loading glucose entries:', error);
      showToast.error('Ошибка загрузки записей уровня глюкозы');
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditingId(null);
    editForm.resetFields();
  };

  const handleEdit = (record: any) => {
    setEditingId(record.id);
    editForm.setFieldsValue({
      glucose_level: record.glucose_level,
      meal_type: record.meal_type,
      measurement_time: record.measurement_time,
    });
    setModalVisible(true);
  };

  const saveEntry = async (values: any, entryId: number | null) => {
    const data = {
      glucose_level: values.glucose_level,
      meal_type: values.meal_type,
      measurement_time: values.measurement_time,
    };
    if (entryId) {
      await diaryAPI.updateGlucoseEntry(entryId, data);
      showToast.success('Уровень глюкозы успешно обновлен!');
    } else {
      await diaryAPI.createGlucoseEntry(data);
      showToast.success('Уровень глюкозы успешно добавлен!');
    }
  };

  const onFinish = async (values: any) => {
    try {
      await saveEntry(values, null);
      form.resetFields();
      await loadEntries();
    } catch (error: any) {
      console.error('Error saving glucose entry:', error);
      showToast.error(error.response?.data?.detail || 'Ошибка сохранения уровня глюкозы');
    }
  };

  const onEditFinish = async (values: any) => {
    try {
      await saveEntry(values, editingId);
      closeModal();
      await loadEntries();
    } catch (error: any) {
      console.error('Error saving glucose entry:', error);
      showToast.error(error.response?.data?.detail || 'Ошибка сохранения уровня глюкозы');
    }
  };

  const renderFormFields = () => (
    <Row gutter={16}>
      <Col span={24} md={8}>
        <Form.Item
          label="Уровень глюкозы (ммоль/л)"
          name="glucose_level"
          rules={[{ required: true, message: 'Введите уровень глюкозы!' }]}
        >
          <InputNumber
            placeholder="Введите уровень глюкозы"
            min={0}
            max={30}
            precision={1}
            step={0.1}
            style={{ width: '100%' }}
            size="large"
            {...decimalInputNumberProps}
          />
        </Form.Item>
      </Col>

      <Col span={24} md={8}>
        <Form.Item
          label="Прием пищи"
          name="meal_type"
          rules={[{ required: true, message: 'Выберите прием пищи!' }]}
        >
          <Select
            placeholder="Выберите прием пищи"
            options={mealTypes}
            size="large"
          />
        </Form.Item>
      </Col>

      <Col span={24} md={8}>
        <Form.Item
          label="Время измерения"
          name="measurement_time"
          rules={[{ required: true, message: 'Выберите время измерения!' }]}
        >
          <Select
            placeholder="Выберите время измерения"
            options={measurementTimes}
            size="large"
          />
        </Form.Item>
      </Col>
    </Row>
  );

  const columns = [
    {
      title: 'Дата и время',
      key: 'datetime',
      render: (_: any, record: any) => formatDiaryDateTime(record),
    },
    {
      title: 'Уровень глюкозы',
      dataIndex: 'glucose_level',
      key: 'glucose_level',
      render: (value: number) => {
        let color = '#7CB342';
        if (value > 7.0) color = '#ff4d4f';
        else if (value > 6.0) color = '#faad14';

        return <span style={{ color }}>{formatDecimalDisplay(value)} ммоль/л</span>;
      },
    },
    {
      title: 'Прием пищи',
      dataIndex: 'meal_type',
      key: 'meal_type',
      render: (value: string) => mealTypes.find(m => m.value === value)?.label || value,
    },
    {
      title: 'Время измерения',
      dataIndex: 'measurement_time',
      key: 'measurement_time',
      render: (value: string) => measurementTimes.find(m => m.value === value)?.label || value,
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_: any, record: any) => (
        <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small" />
      ),
    },
  ];

  return (
    <div style={{ padding: isMobile ? '0' : '0' }}>
      <div style={{ marginBottom: isMobile ? 16 : 24 }}>
        <Title level={isMobile ? 3 : 2} style={{ color: '#263238', fontSize: isMobile ? '20px' : '24px' }}>Дневник уровня глюкозы</Title>
        <Typography.Text type="secondary" style={{ fontSize: isMobile ? '13px' : '14px' }}>
          Отслеживайте уровень сахара в крови в течение дня
        </Typography.Text>
      </div>

      <Card
        title="Добавить новое измерение"
        style={{ marginBottom: 24, borderRadius: '12px' }}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          {renderFormFields()}
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              icon={<PlusOutlined />}
              loading={loading}
              size="large"
              style={{
                backgroundColor: '#7CB342',
                borderColor: '#7CB342',
                height: '40px',
                borderRadius: '8px'
              }}
            >
              Сохранить уровень сахара
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card title={`Последние измерения (${entries.length})`} style={{ borderRadius: '12px' }} className="slide-in">
        <div style={{ overflowX: 'auto' }}>
          {loading ? (
            <SkeletonLoader type="table" rows={5} />
          ) : (
            <Table
              dataSource={entries}
              columns={columns}
              rowKey="id"
              loading={false}
              pagination={{
                pageSize: isMobile ? 5 : 10,
                showSizeChanger: !isMobile,
                showQuickJumper: !isMobile,
                showTotal: (total) => `Всего ${total} записей`,
                simple: isMobile
              }}
              style={{ borderRadius: '8px', overflow: 'hidden' }}
              scroll={isMobile ? { x: 'max-content' } : undefined}
              size={isMobile ? 'small' : 'middle'}
            />
          )}
        </div>
      </Card>

      <Modal
        title="Редактировать измерение"
        open={modalVisible}
        onCancel={closeModal}
        footer={null}
        width={isMobile ? '100%' : 700}
      >
        <Form form={editForm} layout="vertical" onFinish={onEditFinish}>
          {renderFormFields()}
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" style={{ backgroundColor: '#7CB342', borderColor: '#7CB342' }}>
                Обновить
              </Button>
              <Button onClick={closeModal}>
                Отмена
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
